import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { LanguageType } from '@/types/language';

import type { InquiryCtaSource } from './inquiryCtaSource';
import type { InquirySelectValue } from './inquiryFormValues';
import { useInquiryQueryDefaults } from './useInquiryQueryDefaults';

type Props = {
  children: React.ReactNode;
};

/**
 * ### InquiryFormType
 *
 * Field values of the inquiry form as react-hook-form holds them.
 * Select fields hold the option object while the form is open; `add_data` carries the
 * fields that are persisted as-is with the inquiry.
 */
export interface InquiryFormType {
  writer: string;
  email: string;
  phone: string;
  title: string;
  subtitle: string;
  content: string;
  agreement: string;
  employees: InquirySelectValue;
  /**
   * ### interest
   *
   * Selected field of interest whose option value is an InquiryInterest machine key.
   */
  interest: InquirySelectValue;
  position: string;
  selfReportedAiService: InquirySelectValue;
  selfReportedSource: InquirySelectValue;
  add_data: {
    marketing: string;
    language: LanguageType;
    /**
     * ### source
     *
     * Call-to-action that led the visitor here, present only when the query named a known one.
     */
    source?: InquiryCtaSource;
  };
}

export default function InquiryFormProvider({ children }: Props) {
  const methods = useForm<InquiryFormType>({
    defaultValues: {
      writer: '',
      email: '',
      phone: '',
      title: '',
      subtitle: '',
      content: '',
      agreement: '',
      employees: '',
      position: '',
      interest: '',
      selfReportedAiService: '',
      selfReportedSource: '',
      add_data: {
        marketing: '',
        language: undefined,
      },
    },
    mode: 'all',
  });

  useInquiryQueryDefaults(methods);

  return <FormProvider {...methods}>{children}</FormProvider>;
}
